import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CartAddedToast } from '../components/CartAddedToast/CartAddedToast';
import {
  CART_STORAGE_KEY,
  DIGIRASA_CART_CLEARED_EVENT,
} from './cartStorage';

const CartContext = createContext();

const CART_TOAST_DURATION_MS = 5500;

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  const [addedId, setAddedId] = useState(null);
  const [cartToast, setCartToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  // Logout clears storage then fires this event so in-memory items cannot be
  // written back by the effect above.
  useEffect(() => {
    const onCartCleared = () => {
      setCartItems((prev) => (prev.length === 0 ? prev : []));
      setAddedId(null);
    };
    window.addEventListener(DIGIRASA_CART_CLEARED_EVENT, onCartCleared);
    return () => window.removeEventListener(DIGIRASA_CART_CLEARED_EVENT, onCartCleared);
  }, []);

  const dismissCartToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setCartToast(null);
  }, []);

  const showCartToast = useCallback(
    ({ name, isRepeat, cartCount }) => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }

      setCartToast({
        name: name || 'Item',
        isRepeat,
        cartCount,
        key: Date.now(),
      });

      toastTimerRef.current = window.setTimeout(() => {
        toastTimerRef.current = null;
        setCartToast(null);
      }, CART_TOAST_DURATION_MS);
    },
    [],
  );

  useEffect(() => () => dismissCartToast(), [dismissCartToast]);

  const addToCart = (item, options = {}) => {
    const { showToast = true } = options;
    const existing = cartItems.find((i) => i.id === item.id);
    const isRepeat = Boolean(existing);
    const nextCount = cartItems.reduce((sum, i) => sum + i.quantity, 0) + 1;

    setCartItems((prev) => {
      const prevExisting = prev.find((i) => i.id === item.id);
      if (prevExisting) {
        return prev.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, { ...item, quantity: 1 }];
    });

    setAddedId(item.id);
    setTimeout(() => setAddedId(null), 1200);

    const onCartPage =
      typeof window !== 'undefined' && window.location.pathname === '/cart';
    if (showToast && !onCartPage) {
      showCartToast({
        name: item.variantLabel
          ? `${item.name || 'Item'} · ${item.variantLabel}`
          : item.name,
        isRepeat,
        cartCount: nextCount,
      });
    }
  };

  const removeFromCart = (id) => {
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQty = (id, qty) => {
    if (qty < 1) {
      removeFromCart(id);
      return;
    }
    setCartItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i)));
  };

  const clearCart = useCallback(() => {
    setCartItems((prev) => (prev.length === 0 ? prev : []));
  }, []);

  /** Replace entire cart (used for reorder). Each item must include `quantity`. */
  const replaceCart = (items) => {
    const next = (Array.isArray(items) ? items : []).map((item) => ({
      ...item,
      quantity: Math.max(1, Math.round(Number(item.quantity) || 1)),
    }));
    setCartItems(next);
    setAddedId(null);
  };

  const cartCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        replaceCart,
        cartCount,
        cartTotal,
        addedId,
      }}
    >
      {children}
      <CartAddedToast toast={cartToast} onDismiss={dismissCartToast} />
    </CartContext.Provider>
  );
};
